/**
 * /downhole/invoices/:id — one tool invoice (fieldiq-api#882
 * `GET /downhole/invoices/:id`). Read only.
 *
 * STORED FIGURES ONLY. The unrouted screen recomputed every line as qty × rate
 * and the subtotal, tax and total from those — the second answer to a question
 * the API answers (CLAUDE.md non-negotiable 5). It also defaulted an absent
 * tax rate to GST 5% and offered a hardcoded GST/HST picker on a tenant that
 * is a US, tax-exempt shop. Here the line amounts, subtotal, tax rate, tax
 * amount and total are the invoice's own; an absent one is `—`, never 0 and
 * never 5%.
 *
 * Every money figure is in the INVOICE's currency.
 *
 * WRITES (api#910, through unified-invoices):
 *   Download PDF   admin, office_staff, dispatcher — the API's PDF, as a file
 *   Send           admin, office_staff — emails the PDF to the customer's
 *                  accounting contact (else main contact); the API names who
 *                  it went to. Not offered on a void or paid invoice.
 *   Void           admin ONLY (the API's guard is strict). Frees the runs.
 *   Rebill         void, then a new draft from the same runs — there is no
 *                  rebill route. Offered on a void invoice, and as "Void and
 *                  rebill" in the void confirm.
 * NOT offered: status changes through PATCH. That route does not check the
 * current status, so it can move a void invoice back to draft and re-lock runs
 * that have been billed again since. Approve and Mark paid stay off ("paid" is
 * a bare flag with no payment behind it, per the operator). ERP sync is not
 * planned.
 *
 * TAX is the stored snapshot: its labels (GST, PST, state …), percents and
 * amounts. When the jurisdiction's own row was not used (`tax_fallback_reason`
 * jurisdiction_absent / not_yet_effective) the API refuses Send, and the page
 * says why and what to do before anyone presses it.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, CalendarClock, CircleDollarSign, Download, Loader2, Percent, Receipt, Repeat, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage, getBlobApiErrorMessage } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useInvoiceRecipient, useSendInvoice, useVoidInvoice } from '../../hooks/useDownhole';
import { CcField, RecipientPreview, parseCcInput } from './sendBits';
import { onlineOrSay } from '../../lib/downhole-offline';
import { canReadDownholeInvoicePdf, canVoidDownholeInvoices, canWriteDownholeInvoices } from '../../lib/downhole-roles';
import type { DhInvoiceDetail } from '../../types/downhole';
import { DH_SECONDARY, DhDialog, useOpenCount } from './DownholeBits';
import { GenerateInvoiceDialog } from './InvoiceDialogs';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeInvoice } from '../../hooks/useDownhole';
import { formatInstant, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtQty } from '../../lib/display-labels';
import { DH_INVOICE_STATUS_SKIN } from '../../lib/downhole-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DhBadge, DhEmpty, DhPanel, DhSpec } from './DownholeBits';
import { Tooltip } from '../../components/ui/tooltip';

/** A secondary action on the band: white/25 edge, no colour class (CLAUDE.md, the band). */
const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50';

/** The fallback reasons the API refuses Send on (api#910 TAX_FALLBACK_BLOCKED). */
const SEND_BLOCKING_FALLBACK = ['jurisdiction_absent', 'not_yet_effective'];

/** Every run this invoice bills — the header run for a single-run invoice, else each line's. */
export function runsOnInvoice(inv: DhInvoiceDetail): string[] {
  const ids = new Set<string>();
  if (inv.run_log_id) ids.add(inv.run_log_id);
  for (const l of inv.line_items) if (l.run_log_id) ids.add(l.run_log_id);
  return Array.from(ids);
}

/**
 * The invoice's actions: the buttons on the band, and their dialogs IN THE
 * BODY. The dialogs used to live inside the band's actions slot with the
 * buttons, and "Void and rebill" opened the rebill there — inside the band's
 * own stacking and clipping, and torn down with the button that owned it. The
 * state is held by the screen, so a dialog outlives the button that opened it:
 * Void removes the Void button, and the rebill it opens stays open.
 */
function useInvoiceActions() {
  const [confirm, setConfirm] = useState<'send' | 'void' | null>(null);
  const [rebill, setRebill] = useState(false);
  return { confirm, setConfirm, rebill, setRebill };
}
type InvoiceActionState = ReturnType<typeof useInvoiceActions>;

function InvoiceActionButtons({ inv, state }: { inv: DhInvoiceDetail; state: InvoiceActionState }) {
  const { user } = useAuth();
  const [pdfBusy, setPdfBusy] = useState(false);
  const canWrite = canWriteDownholeInvoices(user?.role);
  const canPdf = canReadDownholeInvoicePdf(user?.role);
  const canVoid = canVoidDownholeInvoices(user?.role);
  const isVoid = inv.status === 'void';
  const sendable = canWrite && !isVoid && inv.status !== 'paid';
  const sendBlocked = SEND_BLOCKING_FALLBACK.includes(inv.tax_fallback_reason ?? '') && !inv.tax_exempt;
  const runIds = runsOnInvoice(inv);

  const downloadPdf = async () => {
    if (!onlineOrSay(toast.error)) return;
    setPdfBusy(true);
    try {
      const res = await api.downhole.invoices.pdf(inv.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number || 'invoice'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      toast.error(await getBlobApiErrorMessage(error, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  if (!canPdf && !canWrite) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {canPdf && (
        <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy}>
          {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
          PDF
        </button>
      )}
      {canVoid && !isVoid && inv.status !== 'paid' && (
        <button type="button" className={BAND_SECONDARY} onClick={() => state.setConfirm('void')}>
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && isVoid && runIds.length > 0 && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => state.setRebill(true)}>
          <Repeat className="size-4" aria-hidden="true" />
          Rebill these runs
        </button>
      )}
      {sendable && (
        <Tooltip content={sendBlocked ? 'Tax for this province or state is not set up. See the note below.' : undefined}>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => state.setConfirm('send')}
            disabled={sendBlocked}
          >
            <Send className="size-4" aria-hidden="true" />
            {inv.sent_at ? 'Send again' : 'Send to customer'}
          </button>
        </Tooltip>
      )}
    </div>
  );
}

function InvoiceActionDialogs({ inv, tz, state }: { inv: DhInvoiceDetail; tz: string | null; state: InvoiceActionState }) {
  const { user } = useAuth();
  const send = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const canWrite = canWriteDownholeInvoices(user?.role);
  const runIds = runsOnInvoice(inv);
  const { confirm, setConfirm, rebill, setRebill } = state;
  // API-A: who it goes to, read while the confirm is open (the send's own resolver).
  const preview = useInvoiceRecipient(inv.id, confirm === 'send');
  const noAddress = !!preview.data?.data && !preview.data.data.to;
  const [ccText, setCcText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const opened = useOpenCount(confirm === 'send');
  React.useEffect(() => {
    if (opened === 0) return;
    setCcText('');
    setSendError(null);
  }, [opened]);
  const cc = parseCcInput(ccText);

  const doSend = async () => {
    if (cc.problem || noAddress) return;
    if (!onlineOrSay(toast.error)) return;
    setSendError(null);
    try {
      const res = await send.mutateAsync({ id: inv.id, ...(cc.cc.length ? { cc: cc.cc } : {}) });
      const copied = res.data?.sent_cc?.length ? `, copied to ${res.data.sent_cc.join(', ')}` : '';
      toast.success(`${inv.invoice_number ?? 'Invoice'} sent to ${res.data?.sent_to ?? 'the customer'}${copied}`);
      setConfirm(null);
    } catch (error) {
      // CC_INVALID and NO_BILLING_EMAIL as the API worded them, in the dialog.
      setSendError(getApiErrorMessage(error, 'The invoice was not sent.'));
    }
  };

  const doVoid = async (thenRebill: boolean) => {
    if (!onlineOrSay(toast.error)) return;
    try {
      await voidInvoice.mutateAsync(inv.id);
      toast.success(`${inv.invoice_number ?? 'Invoice'} voided`);
      setConfirm(null);
      if (thenRebill) setRebill(true);
    } catch (error) {
      setConfirm(null);
      toast.error(getApiErrorMessage(error, 'The invoice was not voided.'));
    }
  };

  return (
    <>
      <DhDialog
        open={confirm === 'send'}
        title={`Send ${inv.invoice_number ?? 'this invoice'}?`}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
              disabled={send.isPending || noAddress || !!cc.problem || !preview.data}
              onClick={doSend}
            >
              {send.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Send
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-fieldiq-text">
            The PDF is emailed to {inv.customer_name || 'the customer'}&rsquo;s accounting contact, or their main contact if there is none.
            {inv.status === 'draft' ? ' The invoice moves to Sent once the email is accepted.' : ''}
          </p>
          <RecipientPreview query={preview} tz={tz} subject="who this invoice goes to" />
          {noAddress && (
            <p className="text-xs text-fieldiq-muted">
              Add an accounting or main contact email on{' '}
              {inv.customer_id ? (
                <Link to={`/customers/${inv.customer_id}?edit=true`} className="font-bold text-fieldiq-accent-signal underline">
                  the customer
                </Link>
              ) : (
                'the customer'
              )}
              , then send.
            </p>
          )}
          {sendError && (
            <p role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-dark px-4 py-3 text-sm font-bold text-fieldiq-text">
              {sendError}
            </p>
          )}
          <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
        </div>
      </DhDialog>

      <DhDialog
        open={confirm === 'void'}
        title={`Void ${inv.invoice_number ?? 'this invoice'}?`}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button type="button" className={DH_SECONDARY} disabled={voidInvoice.isPending} onClick={() => doVoid(false)}>
              {voidInvoice.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Void
            </button>
            {canWrite && runIds.length > 0 && (
              <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" disabled={voidInvoice.isPending} onClick={() => doVoid(true)}>
                Void and rebill
              </button>
            )}
          </>
        }
      >
        <p className="text-sm text-fieldiq-text">
          The invoice stays on record, marked void, and its {runIds.length} {runIds.length === 1 ? 'run becomes' : 'runs become'} free to bill
          again. A voided invoice cannot be un-voided. To correct it, void and rebill: a new draft is made from the same runs, at the prices the
          customer approved on each run&rsquo;s field ticket.
        </p>
      </DhDialog>

      {rebill && (
        <GenerateInvoiceDialog open onClose={() => setRebill(false)} tz={tz} presetRunIds={runIds} title={`Rebill ${inv.invoice_number ?? ''}`.trim()} />
      )}
    </>
  );
}

const TH = 'px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const TD = 'px-4 py-3 text-sm text-fieldiq-text';
const TD_NUM = 'whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text';

/** tax_rate is a PERCENT as stored (8.25 → "8.25%"). Absent → `—`, never 0% or 5%. */
const taxRate = (v: number | null | undefined) => {
  const text = fmtQty(v);
  return text === '—' ? text : `${text}%`;
};

export default function InvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useDownholeInvoice(id);
  const failure = queryLoadFailure(query);
  const inv = query.data?.data ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const cur = inv?.currency ?? null;
  const loading = query.isLoading;
  const actions = useInvoiceActions();
  const canRebill = canWriteDownholeInvoices(useAuth().user?.role);

  return (
    <div data-testid="downhole-invoice-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={inv ? `${inv.invoice_number ?? 'Tool invoice'}${inv.status === 'void' ? ' · Void' : ''}` : 'Tool invoice'}
        title={inv ? inv.customer_name || 'No customer on file' : loading ? 'Loading…' : 'Invoice'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={inv ? <InvoiceActionButtons inv={inv} state={actions} /> : undefined}
        stats={
          failure || (!loading && !inv)
            ? undefined
            : [
                { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Subtotal', sub: 'Before tax', tone: 'neutral', loading, value: fmtCurrency(inv?.subtotal, cur) },
                { icon: <Percent className="size-4" aria-hidden="true" />, label: 'Tax', sub: `At ${taxRate(inv?.tax_rate)}`, tone: 'neutral', loading, value: fmtCurrency(inv?.tax_amount, cur) },
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: 'Total', sub: cur ?? 'Currency —', tone: 'accent', loading, value: fmtCurrency(inv?.total, cur) },
                {
                  icon: <CalendarClock className="size-4" aria-hidden="true" />,
                  label: 'Due',
                  // Month and day on the figure, the year beneath: the full date
                  // clipped to "Oct 29, 202" in a half-width column on a phone.
                  sub: inv?.due_date ? `Payment due ${formatInstantTenantDay(inv.due_date, tz, { year: 'numeric' }) ?? ''}`.trim() : 'Payment due',
                  tone: 'info',
                  loading,
                  value: formatInstantTenantDay(inv?.due_date, tz, { month: 'short', day: 'numeric' }) ?? '—',
                },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading invoice" />
            </div>
          ) : failure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this invoice" />
            </div>
          ) : !inv ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <DhEmpty>This invoice does not exist.</DhEmpty>
            </div>
          ) : (
            <>
              {inv.status === 'void' && (
                <div
                  role="status"
                  data-testid="downhole-invoice-void-notice"
                  className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text"
                >
                  <DhBadge map={DH_INVOICE_STATUS_SKIN} value="void" />
                  <p className="min-w-0 flex-1 basis-64">
                    <span className="font-bold">This invoice is void.</span> It stays on record and will not be sent or paid; its{' '}
                    {runsOnInvoice(inv).length === 1 ? 'run is' : 'runs are'} free to bill again.
                  </p>
                  {canRebill && runsOnInvoice(inv).length > 0 && (
                    <button type="button" className={DH_SECONDARY} onClick={() => actions.setRebill(true)}>
                      <Repeat className="size-4" aria-hidden="true" />
                      Rebill these runs
                    </button>
                  )}
                </div>
              )}
              {SEND_BLOCKING_FALLBACK.includes(inv.tax_fallback_reason ?? '') && !inv.tax_exempt && inv.status !== 'void' && (
                <div role="note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">
                    Tax for {inv.jurisdiction || 'this province or state'}{' '}
                    {inv.tax_fallback_reason === 'not_yet_effective' ? 'is not in effect yet on the invoice date' : 'is not set up'}, so the company&rsquo;s
                    default rate was used and this invoice cannot be sent.
                  </p>
                  <p className="mt-1">
                    Add the rate in{' '}
                    <Link to="/settings/regional-tax" className="font-bold text-fieldiq-accent-signal underline">
                      Settings → Regional &amp; Tax
                    </Link>
                    , then void this invoice and rebill its runs.
                  </p>
                </div>
              )}
              <DhPanel eyebrow="Invoice" title={inv.invoice_number || 'Invoice'}>
                <div className="px-4 pt-4">
                  <DhBadge map={DH_INVOICE_STATUS_SKIN} value={inv.status} />
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-4">
                  <DhSpec label="Customer" value={displayValue(inv.customer_name)} />
                  {/* A Directional & MWD daily-ticket invoice bills a well job, not
                      an assembly's run, so its Assembly and Run would only ever read
                      "—". The read names the job (dh_job_id + job_number) on those
                      invoices only: null on a run invoice, absent from an older API,
                      and both mean "not a well-job invoice". */}
                  {inv.dh_job_id && inv.job_number ? (
                    <DhSpec
                      label="Well job"
                      value={
                        <Link to={`/downhole/wells/${inv.dh_job_id}`} className="font-mono text-fieldiq-accent-signal hover:underline">
                          Well job {inv.job_number}
                        </Link>
                      }
                    />
                  ) : (
                    <>
                      <DhSpec
                        label="Assembly"
                        value={
                          inv.assembly_id ? (
                            <Link to={`/downhole/assemblies/${inv.assembly_id}/build`} className="font-mono text-fieldiq-accent-signal hover:underline">
                              {inv.assembly_number || '—'}
                            </Link>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <DhSpec
                        label="Run"
                        value={
                          inv.run_log_id ? (
                            <Link to={`/downhole/run-logs/${inv.run_log_id}`} className="text-fieldiq-accent-signal hover:underline">
                              Run {inv.run_number ?? '—'}
                            </Link>
                          ) : (
                            '—'
                          )
                        }
                      />
                    </>
                  )}
                  <DhSpec label="Well / Rig" value={inv.well_name || inv.rig_name ? `${inv.well_name || '—'} / ${inv.rig_name || '—'}` : '—'} />
                  <DhSpec label="Created" value={formatInstant(inv.created_at, tz) ?? '—'} />
                  <DhSpec label="Sent" value={formatInstant(inv.sent_at, tz) ?? '—'} />
                  <DhSpec label="Paid" value={formatInstant(inv.paid_at, tz) ?? '—'} />
                  <DhSpec label="Currency" value={displayValue(inv.currency)} />
                  <DhSpec label="Province / state" value={displayValue(inv.jurisdiction)} />
                </dl>
                {/* The notes carry api#927's rate-card note — "Billed at the prices the
                    customer approved…", one line per run whose price today differs —
                    so the line breaks are kept. */}
                {inv.notes && (
                  <div data-testid="downhole-invoice-notes" className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Notes</p>
                    <p className="mt-1 whitespace-pre-line">{inv.notes}</p>
                  </div>
                )}
              </DhPanel>

              <DhPanel
                eyebrow="Billing"
                title="Line items"
                testId="downhole-invoice-lines"
                footer={inv.line_items.length ? `${inv.line_items.length} lines` : undefined}
              >
                {inv.line_items.length === 0 ? (
                  <DhEmpty>This invoice has no line items.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px]">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr>
                          <th scope="col" className={TH}>Description</th>
                          <th scope="col" className={TH}>Component</th>
                          <th scope="col" className={`${TH} text-right`}>Qty</th>
                          <th scope="col" className={TH}>Unit</th>
                          <th scope="col" className={`${TH} text-right`}>Rate</th>
                          <th scope="col" className={`${TH} text-right`}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.line_items.map((l) => (
                          <tr key={l.id} className="border-t border-fieldiq-border">
                            <td className={`${TD} max-w-[28rem]`}>{displayValue(l.description)}</td>
                            <td className={`${TD} font-mono`}>{l.component_serial || '—'}</td>
                            <td className={TD_NUM}>{fmtQty(l.quantity)}</td>
                            <td className={TD}>{displayValue(l.unit)}</td>
                            <td className={TD_NUM}>{fmtCurrency(l.rate, cur)}</td>
                            <td className={`${TD_NUM} font-extrabold text-white`}>{fmtCurrency(l.amount, cur)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <dl className="ml-auto w-full max-w-sm space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-fieldiq-muted">Subtotal</dt>
                    <dd className="font-mono text-fieldiq-text">{fmtCurrency(inv.subtotal, cur)}</dd>
                  </div>
                  {/* The stored tax lines (GST, PST, state …) when the snapshot names
                      them; otherwise the one stored rate. Never recomputed. */}
                  {inv.tax_rate_1_label || inv.tax_rate_2_label ? (
                    [
                      [inv.tax_rate_1_label, inv.tax_rate_1_percent, inv.tax_rate_1_amount],
                      [inv.tax_rate_2_label, inv.tax_rate_2_percent, inv.tax_rate_2_amount],
                    ]
                      .filter(([label]) => !!label)
                      .map(([label, pct, amt]) => (
                        <div key={String(label)} className="flex justify-between">
                          <dt className="text-fieldiq-muted">
                            {String(label)} ({taxRate(pct as number | null)})
                          </dt>
                          <dd className="font-mono text-fieldiq-text">{fmtCurrency(amt as number | null, cur)}</dd>
                        </div>
                      ))
                  ) : (
                    <div className="flex justify-between">
                      <dt className="text-fieldiq-muted">Tax ({taxRate(inv.tax_rate)})</dt>
                      <dd className="font-mono text-fieldiq-text">{fmtCurrency(inv.tax_amount, cur)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-fieldiq-border pt-1 font-extrabold">
                    <dt className="text-white">Total</dt>
                    <dd className="font-mono text-white">{fmtCurrency(inv.total, cur)}</dd>
                  </div>
                </dl>
              </DhPanel>
            </>
          )}
        </div>
      </section>
      {inv && <InvoiceActionDialogs inv={inv} tz={tz} state={actions} />}
    </div>
  );
}
